/* eslint-disable no-restricted-globals */
import React, { useState, useCallback, useEffect } from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import DialogActions from '@mui/material/DialogActions';
import Switch from '@mui/material/Switch';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Slider from '@mui/material/Slider';
import {
  Check,
  Clear,
  HighlightOff,
} from '@mui/icons-material';
import { styled } from '@mui/styles';
import format from 'date-fns/format';

import Chip from '@mui/material/Chip';

import { useDispatch } from 'react-redux';

import { types } from './survey-types';

export const NumberInput = ({ change, setFieldChange }) => {
  return (
    <Grid item xs={12}>
      <TextField
        placeholder="Input Number"
        variant="outlined"
        value={change.number_value == null ? '' : change.number_value}
        onChange={(e) =>
          setFieldChange({
            ...change,
            number_value: isNaN(parseFloat(e.target.value))
              ? ''
              : e.target.value,
          })
        }
      />
    </Grid>
  );
};

const MarginChip = styled(Chip)({
  marginRight: 6,
  marginTop: 6,
});

export const CategoryInput = ({
  change,
  setFieldChange,
  categoryOptions: values,
}) => {
  const [text, setText] = React.useState('');

  const addCategory = useCallback(() => {
    if ((change.categories || []).indexOf(text) === -1) {
      setFieldChange({
        ...change,
        categories: (change.categories || []).concat(text),
      });
    }
    setText('');
  }, [text, change, setFieldChange]);

  const selectCategory = useCallback(
    (add) => {
      setFieldChange({
        ...change,
        categories: (change.categories || []).concat(add),
      });
    },
    [change, setFieldChange]
  );

  const removeCategory = useCallback(
    (remove) => {
      setFieldChange({
        ...change,
        categories: (change.categories || []).filter((cat) => cat !== remove),
      });
    },
    [change, setFieldChange]
  );

  return (
    <Grid
      item
      container
      xs={12}
      style={{
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <Grid item xs={12} style={{ display: 'flex', alignItems: 'center' }}>
        <TextField
          label="Categories"
          variant="outlined"
          placeholder="Choose Categories"
          value={text}
          onChange={(e) => setText(e.target.value || '')}
        />
        <Button onClick={addCategory}>Add Category</Button>
        <br />
      </Grid>
      <Grid item xs={12}>
        {(change.categories || [])
          .filter((val) => val.includes(text))
          .map((cat) => (
            <MarginChip
              key={cat}
              label={cat}
              onDelete={() => removeCategory(cat)}
              color="primary"
            />
          ))}
        {Array.from(new Set(values || []))
          .filter((val) => !text || val.includes(text))
          .filter((val) => (change.categories || []).indexOf(val) === -1)
          .map((cat) => (
            <MarginChip
              key={cat}
              label={cat}
              onClick={() => selectCategory(cat)}
              clickable
            />
          ))}
      </Grid>
    </Grid>
  );
};

export const BooleanInput = ({ change, setFieldChange }) => {
  const checked = Boolean(change.number_value);
  useEffect(() => {
    if (change.number_value == null) {
      setFieldChange({
        ...change,
        number_value: 0,
      });
    }
  });
  return (
    <Grid
      item
      xs={12}
      style={{
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <Clear />
      <Switch
        checked={checked}
        onChange={() =>
          setFieldChange({
            ...change,
            number_value: checked ? 0 : 1,
          })
        }
        color="primary"
        inputProps={{ 'aria-label': 'Change values' }}
      />
      <Check />
    </Grid>
  );
};

export const RangeInput = ({ change, setFieldChange }) => {
  useEffect(() => {
    if (change.number_value == null) {
      setFieldChange({
        ...change,
        number_value: 0,
      });
    }
  });
  return (
    <Grid
      item
      xs={12}
      style={{
        display: 'flex',
        alignItems: 'bottom',
      }}
    >
      <Slider
        style={{ paddingTop: 42 }}
        defaultValue={0}
        value={change.number_value || 0}
        getAriaValueText={() => change.number_value}
        aria-labelledby="discrete-slider"
        valueLabelDisplay="auto"
        step={1}
        marks
        min={0}
        max={10}
        onChange={(e, value) => {
          setFieldChange({
            ...change,
            number_value: value,
          });
        }}
      />
      <Typography
        variant="h5"
        style={{
          width: 64,
          paddingTop: 28,
          textAlign: 'center',
        }}
      >
        {change.number_value || 0}
      </Typography>
    </Grid>
  );
};

export const TextInput = ({ change, setFieldChange }) => {
  return (
    <Grid item xs={12}>
      <TextField
        placeholder="Input Text"
        variant="outlined"
        value={change.text_value || ''}
        rows="4"
        fullWidth
        multiline
        onChange={(e) =>
          setFieldChange({
            ...change,
            text_value: e.target.value,
          })
        }
      />
    </Grid>
  );
};

const getInput = ({
  selectedField,
  setFieldChange,
  categoryOptions,
  change,
}) => {
  switch (selectedField.type) {
    case types.RANGE:
      return <RangeInput setFieldChange={setFieldChange} change={change} />;
    case types.CATEGORIES:
      return (
        <CategoryInput
          setFieldChange={setFieldChange}
          categoryOptions={categoryOptions}
          change={change}
        />
      );
    case types.TEXT:
      return <TextInput setFieldChange={setFieldChange} change={change} />;
    case types.YES_OR_NO:
      return <BooleanInput setFieldChange={setFieldChange} change={change} />;
    case types.NUMBER:
      return <NumberInput setFieldChange={setFieldChange} change={change} />;
    default:
      return <TextInput setFieldChange={setFieldChange} change={change} />;
  }
};

export const FieldInput = ({
  open,
  survey,
  selectedField,
  categoryOptions,
  selectedSurveyParcel,
  values,
  setValues,
  handleClose = () => {},
}) => {
  const dispatch = useDispatch();
  const [change, setFieldChange] = useState({});
  const finish = useCallback(() => {
    setFieldChange({});
    handleClose();
  }, [handleClose]);

  const save = useCallback(() => {
    dispatch({
      type: 'CREATE_FIELD_CHANGE',
      payload: {
        survey,
        fieldDefinition: selectedField,
        fieldChange: {
          ...change,
          survey_parcel_id: selectedSurveyParcel.id,
        },
      },
    });
    finish();
  }, [selectedSurveyParcel, survey, selectedField, change, finish, dispatch]);

  if (!open) return null;

  return (
    <Dialog open={open} aria-labelledby="dialog-title">
      <DialogTitle id="dialog-title">
        Track:&nbsp;
        {selectedField.name}
      </DialogTitle>

      <DialogContent>
        <Grid container spacing={2}>
          {getInput({
            setFieldChange,
            categoryOptions,
            change,
            selectedField,
            values,
          })}
          <Grid item xs={12}>
            <TextField
              variant="outlined"
              placeholder="More details"
              rows="4"
              fullWidth
              value={change.details || ''}
              multiline
              onChange={(e) =>
                setFieldChange({
                  ...change,
                  details: e.target.value,
                })
              }
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={finish}>
          Close
        </Button>
        <Button
          onClick={save}
          color="primary"
          variant="contained"
          disabled={
            !change.text_value &&
            isNaN(parseFloat(change.number_value)) &&
            !(change.categories || []).length
          }
        >
          Save
        </Button>
      </DialogActions>
      {values.length ? (
        <History
          values={values}
          setValues={setValues}
          survey={survey}
          surveyParcelId={selectedSurveyParcel.id}
        />
      ) : null}
    </Dialog>
  );
};

const History = ({ values, survey, surveyParcelId, setValues }) => {
  const dispatch = useDispatch();
  return (
    <DialogContent>
      <Grid container spacing={1}>
        <Grid item xs={12}>
          <Typography variant="subtitle2">History</Typography>
        </Grid>
        {values.map((value, i) => [
          <Grid item xs={11} key={`${value.id}-value`}>
            <Typography variant="h6">
              {value.number_value || value.text_value}
            </Typography>
          </Grid>,
          <Grid item xs={1} key={`${value.id}-del`}>
            <HighlightOff
              style={{
                cursor: 'pointer',
              }}
              onClick={() => {
                dispatch({
                  type: 'DELETE_FIELD_CHANGE',
                  payload: {
                    fieldChange: value,
                    survey,
                    surveyParcelId,
                  },
                });
                setValues(values.filter(({ id }) => value.id !== id));
              }}
            />
          </Grid>,
          <Grid item xs={11} key={`${value.id}-date`}>
            <Typography variant="subtitle2">
              {format(new Date(value.created), 'h:mma MMM do, yyyy')}
            </Typography>
          </Grid>,
          value.details ? (
            <Grid item xs={12} key={`${value.id}-details`}>
              <Typography variant="body2">{value.details}</Typography>
            </Grid>
          ) : null,
          i === values.length - 1 ? null : (
            <Grid item xs={12} key={`${value.id}-hr`}>
              <hr />
            </Grid>
          ),
        ])}
      </Grid>
    </DialogContent>
  );
};
